import moment from 'moment';

export type TimePeriodOption = {
    // The selected quarter's last instant (e.g. 2026-06-30T23:59:59.999Z for Apr–Jun 2026) —
    // computed from, not copied from, the engine-supplied dates. This is deliberate: two
    // different views (e.g. a site's own available dates vs the organisation's) can have
    // slightly different exact "latest run" timestamps for the same quarter — a site's own
    // last commit within a run vs the run's overall latest commit across every site — so using
    // either verbatim would make the same quarter look unselected when switching views. Anchoring
    // on the quarter boundary instead makes `value` identical everywhere the same quarter appears.
    value: string;
    label: string;
};

const QUARTER_LABELS = ['Jan – Mar', 'Apr – Jun', 'Jul – Sep', 'Oct – Dec'];

// Guards against a tampered/manually-edited URL — e.g. a browser normalising an
// un-encoded "+" offset separator into a space — reaching Redux or a GraphQL query.
export const isValidTimePeriodAt = (value: string): boolean => Boolean(value) && moment(value, moment.ISO_8601, true).isValid();

// Shared by useGnrFilters and useSelectedTimePeriod — the only two places that read `at` back
// out of the URL — so a tampered value is dropped the same way on both the portfolio and
// site-level route trees.
export const parseValidatedAtParam = (searchParams: URLSearchParams, paramName = 'at'): string | null => {
    const raw = searchParams.get(paramName);
    return raw && isValidTimePeriodAt(raw) ? raw : null;
};

// Buckets ISO dates into calendar quarters, one option per quarter with any data, ordered
// latest first. The engine already de-dupes to one date per day — quarter-bucketing for
// display is a frontend-only concern.
export const groupDatesIntoQuarters = (dates: string[]): TimePeriodOption[] => {
    const quarterKeys = new Set(
        dates.map(date => {
            const utcDate = moment.utc(date);
            return `${utcDate.year()}-${utcDate.quarter()}`;
        })
    );

    return Array.from(quarterKeys)
        .map(quarterKey => {
            const [year, quarter] = quarterKey.split('-').map(Number);
            const quarterStart = moment.utc({ year }).quarter(quarter);
            return {
                value: quarterStart.endOf('quarter').toISOString(),
                label: `${QUARTER_LABELS[quarter - 1]} ${year}`,
                sortKey: quarterStart.valueOf(),
            };
        })
        .sort((a, b) => b.sortKey - a.sortKey)
        .map(({ value, label }) => ({ value, label }));
};
